import { Button, Icon, MoreMenu, type MenuItem } from '@ui';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { PageActions } from '../../../app/PageActions';
import { useSession } from '../../identity/session';
import type { EntryKind } from '../api';
import { EntryFormDialog } from './EntryFormDialog';

/** Opens the record dialog of an income or expense; render `dialog` once. */
export function useRecordEntry(): { record: (kind: EntryKind) => void; dialog: ReactNode } {
  const navigate = useNavigate();
  const [recording, setRecording] = useState<EntryKind | null>(null);
  return {
    record: setRecording,
    dialog: recording && (
      <EntryFormDialog
        open
        kind={recording}
        onClose={() => setRecording(null)}
        onSaved={(e) => void navigate(`/finanzen/buchungen/${e.id}`)}
      />
    ),
  };
}

/**
 * The finance tabs' actions next to the title: "+ Ausgabe erfassen" and the
 * `…` menu with income and the CSV export (FIN-001, FIN-002, FIN-006).
 */
export function FinanceActions({
  onRecord,
  exportHref,
}: {
  onRecord: (kind: EntryKind) => void;
  exportHref?: string;
}) {
  const { t } = useTranslation();
  const { can } = useSession();
  const write = can('finance.write');
  const items: MenuItem[] = [
    ...(write
      ? [{ key: 'income', label: t('finance.quick.income'), onSelect: () => onRecord('income') }]
      : []),
    ...(exportHref
      ? [
          {
            key: 'export',
            label: t('finance.export'),
            separated: write,
            onSelect: () => window.location.assign(exportHref),
          },
        ]
      : []),
  ];
  return (
    <PageActions>
      {write && (
        <Button iconStart={<Icon name="plus" size="sm" />} onClick={() => onRecord('expense')}>
          {t('finance.quick.expense')}
        </Button>
      )}
      {items.length > 0 && <MoreMenu label={t('common.moreActions')} items={items} />}
    </PageActions>
  );
}
